import React, { useState, useEffect } from 'react';
import { apiFetch } from '../../../utils/apiAdmin';
import { validateProjectMailbox, MAILBOX_DOMAIN } from '../../../utils/projectMailbox';

const TemplateSelectionModal = ({ isOpen, lead, onClose, onConfirm }) => {
    const [templates, setTemplates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedTemplate, setSelectedTemplate] = useState(null);
    const [showConfirmation, setShowConfirmation] = useState(false);
    const [error, setError] = useState(null);
    const [kickoffDate, setKickoffDate] = useState('');
    const [internalOnboarding, setInternalOnboarding] = useState(false);
    // Mandatory at Won (PROJECT-MAILBOX D5/T492). The backend refuses too -
    // this check is convenience, the backend one is the guard.
    const [projectMailbox, setProjectMailbox] = useState('');

    useEffect(() => {
        if (isOpen) {
            fetchTemplates();
        } else {
            // Reset state when modal closes
            setSearchTerm('');
            setSelectedTemplate(null);
            setShowConfirmation(false);
            setError(null);
            setKickoffDate('');
            setInternalOnboarding(false);
            setProjectMailbox('');
        }
    }, [isOpen]);

    const fetchTemplates = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await apiFetch(`${process.env.REACT_APP_API_URL}/admin/templates`);
            const data = await response.json();
            setTemplates(data.templates);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    // Filter templates: main type with linkedWikiId, matching search
    const filteredTemplates = templates.filter(t =>
        t.type === 'main' &&
        t.linkedWikiId &&
        t.name.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const handleCardClick = (template) => {
        setSelectedTemplate(template);
        setShowConfirmation(true);
    };

    const isMonday = (dateString) => {
        if (!dateString) return false;
        const [year, month, day] = dateString.split('-').map(Number);
        const date = new Date(year, month - 1, day);
        return date.getDay() === 1;
    };

    const handleDateChange = (e) => {
        const value = e.target.value;
        setKickoffDate(value);
    };

    const handleConfirm = () => {
        if (selectedTemplate && kickoffDate && isMonday(kickoffDate) && !validateProjectMailbox(projectMailbox)) {
            onConfirm(selectedTemplate.templateId, kickoffDate, internalOnboarding, projectMailbox);
        }
    };

    const validMonday = kickoffDate && isMonday(kickoffDate);
    const mailboxError = validateProjectMailbox(projectMailbox);
    const canConfirm = validMonday && !mailboxError;

    const handleCancelConfirmation = () => {
        setSelectedTemplate(null);
        setShowConfirmation(false);
        setKickoffDate('');
        setInternalOnboarding(false);
        setProjectMailbox('');
    };

    const formatDate = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-GB', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        });
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full mx-4 max-h-[90vh] flex flex-col">
                {/* Header */}
                <div className="px-6 py-4 bg-blue-600 text-white rounded-t-lg flex justify-between items-center">
                    <h2 className="text-xl font-bold">
                        Select Template for {lead?.company}
                    </h2>
                    <button
                        onClick={onClose}
                        className="text-white hover:text-gray-200 text-2xl leading-none"
                    >
                        &times;
                    </button>
                </div>

                <div className="p-6 flex-1 overflow-y-auto">
                    {/* Search input */}
                    <div className="mb-4">
                        <input
                            type="text"
                            placeholder="Search templates..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        />
                    </div>

                    {/* Content */}
                    {loading ? (
                        <div className="flex items-center justify-center py-12">
                            <svg className="animate-spin h-8 w-8 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                        </div>
                    ) : error ? (
                        <div className="text-center py-12 text-red-600">
                            <p>Error loading templates: {error}</p>
                            <button
                                onClick={fetchTemplates}
                                className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                            >
                                Retry
                            </button>
                        </div>
                    ) : filteredTemplates.length === 0 ? (
                        <div className="text-center py-12 text-gray-500">
                            <p className="text-lg">No main templates available</p>
                            <p className="text-sm mt-2">Templates must have a wiki attached to appear here.</p>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {filteredTemplates.map(template => (
                                <div
                                    key={template.templateId}
                                    onClick={() => handleCardClick(template)}
                                    className="p-4 border rounded-lg cursor-pointer hover:border-blue-500 hover:shadow-md transition-all"
                                >
                                    <h3 className="font-medium text-gray-900">{template.name}</h3>
                                    <div className="text-sm text-gray-500 mt-1">
                                        {template.taskCount} tasks
                                    </div>
                                    <div className="text-sm text-gray-500">
                                        Wiki: {template.wikiName}
                                    </div>
                                    {template.addins && template.addins.length > 0 && (
                                        <div className="text-sm text-gray-500">
                                            Add-ins: {template.addins.map(a => a.name).join(', ')}
                                        </div>
                                    )}
                                    <div className="text-xs text-gray-400 mt-2">
                                        Created: {formatDate(template.createdAt)}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Confirmation overlay */}
                {showConfirmation && selectedTemplate && (
                    <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center rounded-lg">
                        <div className="bg-white p-6 rounded-lg shadow-xl max-w-sm mx-4">
                            <h3 className="text-lg font-medium text-gray-900 mb-4">
                                Create project using "{selectedTemplate.name}"?
                            </h3>
                            <p className="text-sm text-gray-500 mb-4">
                                This will clone {selectedTemplate.taskCount} tasks and the wiki for {lead?.company}.
                            </p>
                            <div className="mb-6">
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Project start date (Monday)
                                </label>
                                <input
                                    type="date"
                                    value={kickoffDate}
                                    onChange={handleDateChange}
                                    onKeyDown={(e) => e.preventDefault()}
                                    min="2024-01-01"
                                    step="7"
                                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                                        kickoffDate && !validMonday
                                            ? 'border-red-400 bg-red-50'
                                            : 'border-gray-300'
                                    }`}
                                />
                                {!kickoffDate && (
                                    <p className="text-xs text-gray-400 mt-1">Select a Monday to continue</p>
                                )}
                                {kickoffDate && !validMonday && (
                                    <p className="text-xs text-red-500 mt-1">Selected date is not a Monday. Please choose a Monday.</p>
                                )}
                            </div>
                            <div className="mb-6">
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Project mailbox
                                </label>
                                <input
                                    type="text"
                                    value={projectMailbox}
                                    onChange={(e) => setProjectMailbox(e.target.value)}
                                    placeholder={`clientname@${MAILBOX_DOMAIN}`}
                                    title="The shared mailbox that receives a copy of every email about this project"
                                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                                        projectMailbox && mailboxError
                                            ? 'border-red-400 bg-red-50'
                                            : 'border-gray-300'
                                    }`}
                                />
                                {!projectMailbox && (
                                    <p className="text-xs text-gray-400 mt-1">Required - the project's shared mailbox on @{MAILBOX_DOMAIN}</p>
                                )}
                                {projectMailbox && mailboxError && (
                                    <p className="text-xs text-red-500 mt-1">{mailboxError}</p>
                                )}
                            </div>
                            <div className="mb-6">
                                <label className="flex items-start gap-2 text-sm font-medium text-gray-700 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={internalOnboarding}
                                        onChange={(e) => setInternalOnboarding(e.target.checked)}
                                        className="mt-0.5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                    />
                                    <span>
                                        Internal client — show temp password instead of emailing
                                        <span className="block text-xs font-normal text-gray-500 mt-0.5">
                                            No welcome email is sent; the password is shown once after creation.
                                        </span>
                                    </span>
                                </label>
                            </div>
                            <div className="flex justify-end space-x-3">
                                <button
                                    onClick={handleCancelConfirmation}
                                    className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 font-medium"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleConfirm}
                                    disabled={!canConfirm}
                                    className={`px-4 py-2 rounded-lg font-medium ${
                                        canConfirm
                                            ? 'bg-blue-600 text-white hover:bg-blue-700'
                                            : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                    }`}
                                >
                                    Confirm
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default TemplateSelectionModal;
